// Зареждането на листа „Доставка" — точно веднъж при отваряне: редакция
// (документът + свързаните стокови), prefill от „Излишък от продажби" или
// фактура над стокови от началния екран. Черновата (от сървъра или локалната,
// с въпрос) е общата — `components/drafts/useDraftResume`, само когато няма
// нито едно от горните (`opensFromDraft`). Зареждането НЕ минава през
// `touch` — не вдига „незапазени промени".
import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import { getDelivery, type Delivery, type DeliveryDocumentType } from "../../../../api/inventory";
import type { DraftRouterState } from "../../components/drafts/draftModel";
import { docTypeOf } from "./deliveryMeta";
import {
  emptyDeliveryRow,
  formatUnitPrice,
  groupDeliveryLines,
  orderOfLines,
  type DeliveryRowDraft,
} from "./deliveryWorksheetShared";

// Изборът от началния екран при незавършена доставка и черновата от сървъра
// (своя или поета от колега) — `DraftRouterState`; плюс собствените входове:
export type DeliveryRouterState = (DraftRouterState & {
  // „Въведи фактура" от стоковите без фактура
  invoiceForNotes?: { supplierId: number | null; noteIds: number[] };
  // „Излишък от продажби" → компенсираща доставка
  prefill?: { productId: number; quantity: number; unitPrice: number | null; warehouseId?: number | null }[];
  prefillDate?: string | null;
}) | null;

// Нов лист без prefill и без фактура над стокови тръгва от черновата (ако има)
export const opensFromDraft = (mode: "new" | "edit", state: DeliveryRouterState) =>
  mode === "new" && !state?.prefill?.length && !state?.invoiceForNotes;

type Actions = {
  setHeader: (header: {
    date: string;
    invoiceNumber: string;
    supplierId: number | null;
    warehouseId: number | null;
    note: string;
    documentType: DeliveryDocumentType;
  }) => void;
  setDate: (date: string) => void;
  setDocumentType: (type: DeliveryDocumentType) => void;
  replaceAllRows: (rows: Record<number, DeliveryRowDraft>, order: number[]) => void;
  setEditLoaded: (loaded: boolean) => void;
  setEditedDelivery: (delivery: Delivery) => void;
  setLinkedNoteIds: (ids: number[]) => void;
  setLinkedNotesExtra: (notes: Delivery[]) => void;
  onLoadError: () => void;
};

type Params = {
  mode: "new" | "edit";
  deliveryId: string | undefined;
  routerState: DeliveryRouterState;
  warehouseId: number | null;
  decimalsOf: (productId: number) => number;
  actions: Actions;
};

export const useDeliveryLoad = ({ mode, deliveryId, routerState, warehouseId, decimalsOf, actions }: Params) => {
  const loadedRef = useRef(false);
  const paramsRef = useRef({ routerState, warehouseId, decimalsOf, actions });
  paramsRef.current = { routerState, warehouseId, decimalsOf, actions };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const { routerState: state, warehouseId: fallbackWarehouse, decimalsOf: decimals, actions: act } =
      paramsRef.current;

    if (mode === "edit") {
      const id = Number(deliveryId);
      if (!id) return;
      getDelivery(id)
        .then((delivery) => {
          act.setHeader({
            // UTC ISO → ЛОКАЛНА дата: документ от 01:00 иначе излиза с вчерашна дата
            date: delivery.date ? dayjs(delivery.date).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
            invoiceNumber: delivery.invoiceNumber ?? "",
            supplierId: delivery.supplierId ?? null,
            warehouseId: delivery.warehouseId ?? fallbackWarehouse,
            note: delivery.note ?? "",
            documentType: docTypeOf(delivery),
          });
          act.setEditedDelivery(delivery);
          const noteIds = delivery.linkedNoteIds ?? [];
          act.setLinkedNoteIds(noteIds);
          // Вече свързаните стокови не са сред „без фактура" — зареждаме ги
          Promise.all(noteIds.map((noteId) => getDelivery(noteId).catch(() => null))).then((notes) =>
            act.setLinkedNotesExtra(notes.filter((n): n is Delivery => !!n)),
          );
          // Записаните суми, не сметнати наново от цената — сумата е въведената
          act.replaceAllRows(
            groupDeliveryLines(delivery, decimals, { keepSavedSums: true }),
            orderOfLines(delivery),
          );
          act.setEditLoaded(true);
        })
        .catch(act.onLoadError);
      return;
    }
    if (state?.prefill?.length) {
      const out: Record<number, DeliveryRowDraft> = {};
      for (const item of state.prefill) {
        const row = emptyDeliveryRow(item.warehouseId ?? fallbackWarehouse);
        row.qty = item.quantity.toFixed(decimals(item.productId)).replace(".", ",");
        row.price = item.unitPrice != null ? formatUnitPrice(item.unitPrice) : "";
        out[item.productId] = row;
      }
      if (state.prefillDate) act.setDate(dayjs(state.prefillDate).format("YYYY-MM-DD"));
      act.replaceAllRows(out, Array.from(new Set(state.prefill.map((p) => p.productId))));
      act.setEditLoaded(true);
      return;
    }
    if (state?.invoiceForNotes) act.setDocumentType("invoice");
  }, [mode, deliveryId]);
};
